import type { LabDetailsData, LabTestDetails } from '@/types/interfaces/sku'
import type { SkuComponentConfigMap, SkuContentAccordionIconKey } from '@/types/enums/sku-component-types'

type ContentAccordionConfig = SkuComponentConfigMap['skuContentAccordion']

/**
 * Maps backend lab icon names (e.g. "IconDrop") to SkuContentAccordionIconKey values.
 * Falls back to 'droplet' for unknown icons.
 */
const LAB_ICON_MAP: Record<string, SkuContentAccordionIconKey> = {
    IconDrop: 'droplet',
    IconFlame: 'flame',
    IconHeart: 'heart',
    IconActivity: 'activity',
    IconPill: 'pill',
    IconFlask: 'flask',
    IconStethoscope: 'stethoscope',
    IconThyroid: 'activity',
    IconKidney: 'activity',
    IconSugar: 'flask',
    IconTube: 'flask',
    IconShield: 'heart',
    IconIron: 'droplet',
    IconBarbell: 'activity',
    IconSun: 'flame',
    IconVirus: 'stethoscope',
    IconWheat: 'pill',
    IconAlarmClock: 'arrowRightCircle',
}

function mapLabIcon(backendIcon: string): SkuContentAccordionIconKey {
    return LAB_ICON_MAP[backendIcon] ?? 'droplet'
}

/**
 * Builds a ContentWAccordion config from a lab SKU's details and test data.
 * Use this at the page level to avoid hardcoding lab accordion configs.
 */
export function buildLabAccordionConfig(
    labDetails: LabDetailsData,
    testDetails: LabTestDetails[],
): ContentAccordionConfig {
    return {
        type: 'skuContentAccordion',
        title: "What's included",
        infoGrid: [
            { label: 'Test type', value: labDetails.test_type },
            { label: 'Collection method', value: labDetails.collection_method },
            { label: 'Fasting', value: labDetails.fasting ? 'Required' : 'Not required' },
            { label: 'Results processing time', value: labDetails.results_processing_time },
        ].filter((cell) => cell.value),
        items: testDetails.map((td) => ({
            title: td.name,
            icon: mapLabIcon(td.icon),
            content: [`<p>${td.description}</p>`, td.includes ? `<p><i>Includes: ${td.includes}</i></p>` : '']
                .filter(Boolean)
                .join(''),
        })),
    }
}
